<template>
  <v-dialog
    :value="value"
    :fullscreen="fullscreen"
    :max-width="maxWidth"
    transition="dialog-bottom-transition"
    overlay-color="var(--ds-color-reverse)"
    @input="closeDialog"
  >
    <v-card>
      <v-toolbar class="px-2">
        <v-toolbar-title class="ds-font-weight--semibold">
          {{ configurationLabel }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <StIcon
            class="close-icon"
            icon-name="close"
            @click="closeDialog"
          />
        </v-toolbar-items>

        <template v-slot:extension>
          <v-tabs
            v-model="tabsModel"
            class="px-5"
            background-color="transparent"
            icons-and-text
            grow
            show-arrows
          >
            <v-tab
              v-for="(tab, index) in allowedTabs"
              :key="index"
              :href="`#tab-${index}`"
            >
              {{ tab.text }}
              <StIcon
                :icon-name="tab.icon"
                class="ma-0"
              />
            </v-tab>
          </v-tabs>
        </template>
      </v-toolbar>

      <v-tabs-items v-model="tabsModel">
        <v-tab-item
          v-for="(tab, index) in allowedTabs"
          :key="index"
          :value="`tab-${index}`"
          @scroll.stop
        >
          <component :is="tab.component" />
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-dialog>
</template>

<script>
import StIcon from '@stilingue/st-icon';
import { mapGetters, mapMutations, mapState } from 'vuex';
import ConfigStyle from '@/components/config/ConfigStyle';
import ConfigShare from '@/components/config/ConfigShare';
import ConfigUsers from '@/components/config/ConfigUsers';
import ConfigContract from '@/components/config/ConfigContract';
import IntegrationToken from '@/components/config/IntegrationToken';
import { MUTATE } from '@/store/modules/mutation_types';
import { Flags } from '@/plugins/rollout';

export default {
  name: 'ConfigModal',
  components: {
    StIcon,
    ConfigStyle,
    ConfigShare,
    ConfigUsers,
    ConfigContract,
    IntegrationToken,
  },
  props: {
    value: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      tabsModel: 0,
    };
  },
  computed: {
    configurationLabel() {
      return this.$t('components.config.ConfigModal.label.title');
    },
    tabsObject() {
      return [
        {
          text: this.$t('components.config.ConfigModal.label.tabs.style'),
          icon: 'eyedropper-variant',
          component: 'ConfigStyle',
        },
        {
          text: this.$t('components.config.ConfigModal.label.tabs.sharing'),
          icon: 'share',
          component: 'ConfigShare',
          featureFlag: Flags.shareDashboard,
        },
        {
          text: this.$t('components.config.ConfigModal.label.tabs.users'),
          icon: 'account-group',
          component: 'ConfigUsers',
        },
        {
          text: this.$t('components.config.ConfigModal.label.tabs.contract'),
          icon: 'account-group',
          component: 'ConfigContract',
          featureFlag: Flags.showBlipComponents,
        },
        {
          text: this.$t('components.config.IntegrationToken.label.title'),
          icon: 'account-lock',
          component: 'IntegrationToken',
        },
      ];
    },
    ...mapState('misc', [
      'showConfig',
      'showConfigTabIndex',
    ]),
    ...mapGetters('dashboard', [
      'activeDashboard',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    allowedTabs() {
      return this.tabsObject.filter(({ featureFlag }) => {
        if (featureFlag) {
          return this.isEnabled(featureFlag);
        }

        return true;
      });
    },
    navBarColor() {
      if (this.activeDashboard.data.color) {
        return this.activeDashboard.data.color;
      }
      return null;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    fullscreen() {
      return this.isMobile;
    },
    maxWidth() {
      if (this.isMobile) return '100%';

      return '80%';
    },
  },
  methods: {
    ...mapMutations('misc', {
      mutateMisc: MUTATE,
    }),
    componentFile(componentName) {
      return () => import(/* webpackPrefetch: true */ `./${componentName}.vue`);
    },
    closeDialog() {
      this.mutateMisc({
        property: 'showConfig',
        with: false,
      });
    },
  },
};
</script>

<style scoped lang="scss">
  h2 {
    color: inherit;
  }

  .close-icon {
    height: 24px;
    padding-top: 24px;
  }

  .v-card {
    border-radius: 8px;
  }
</style>
